// Login surface — the pre-auth landing.
//
// Two-stage flow:
//   1. id + password. If the server returns requireTotp, surface a TOTP
//      input + re-submit with totpCode.
//   2. On success the cookie is set; the parent's useAuth refresh()
//      flips status to logged-in and the app shell takes over.
//
// Branded with the Hub mark + display headline. Tweaks-panel-style
// background gradient matches the main app so the visual transition
// after login feels continuous.

import { useEffect, useState } from "preact/hooks";
import "../styles/login.css";
import { Icon } from "../icons";
import type { UseAuth } from "../lib/use-auth";
import { KidLoginView } from "./kid-login";
import { api } from "../lib/rpc";

export function LoginView({ auth }: { auth: UseAuth }) {
  const [id, setId] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [requireTotp, setRequireTotp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Kid mode availability — probed once on mount via the kid-login
  // GET endpoint. 200 = chat2 host + kid accounts exist; anything else
  // hides the "Kid mode" button on the adult sign-in card.
  const [kidLoginAvailable, setKidLoginAvailable] = useState(false);
  // ?kid=1 in the URL (set by KidApp on sign-out) auto-enters the kid
  // picker — saves the kid an extra tap after one of them is done and
  // hands the tablet off to a sibling.
  const [kidLoginActive, setKidLoginActive] = useState(() => {
    try { return new URL(window.location.href).searchParams.get("kid") === "1"; } catch { return false; }
  });
  // ?local=1 forces the username/password form instead of the default
  // Auth0 redirect. Emergency hatch for admin if Auth0 is unreachable,
  // or for the chat2 LAN-bypass host. Without it, the default path is
  // an immediate redirect to the Auth0 OIDC flow.
  const [localMode] = useState(() => {
    try { return new URL(window.location.href).searchParams.get("local") === "1"; } catch { return false; }
  });

  // Default behavior post-CF-Access: bounce straight to the Auth0 OIDC
  // login. We're no longer behind Cloudflare Access doing the redirect
  // for us, so the SPA initiates it. Skip when:
  //   - kid picker is active (?kid=1)
  //   - local password mode is requested (?local=1)
  // returnTo preserves where the user was headed (mirror-agent validates
  // it's a same-origin relative path before honoring it).
  useEffect(() => {
    if (kidLoginActive || localMode) return;
    const returnTo = window.location.pathname + window.location.search + window.location.hash;
    window.location.href = `/api/oauth/login?returnTo=${encodeURIComponent(returnTo || "/")}`;
  }, [kidLoginActive, localMode]);

  useEffect(() => {
    let cancelled = false;
    api.kidLoginList()
      .then((list) => {
        if (cancelled) return;
        // We still show the button when the list is empty so the
        // user gets the friendly "no kids yet, ask a grown-up" empty
        // state instead of a confusing missing affordance. But hide
        // when the endpoint 404'd (= public hostname).
        setKidLoginAvailable(true);
        void list;
      })
      .catch(() => { if (!cancelled) setKidLoginAvailable(false); });
    return () => { cancelled = true; };
  }, []);

  // Default path is the Auth0 redirect (effect above). While the browser
  // navigates away, render a minimal placeholder rather than flashing the
  // password form. Only kid mode + ?local=1 fall through to real UI.
  if (!kidLoginActive && !localMode) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-brand">
            <div className="login-brand-dot" aria-hidden />
            <span className="login-brand-name">Hub</span>
          </div>
          <p className="login-sub">Taking you to sign in…</p>
        </div>
      </div>
    );
  }

  if (kidLoginActive) {
    return <KidLoginView auth={auth} onBack={() => {
      setKidLoginActive(false);
      // Clear the ?kid=1 flag so a refresh on the adult login doesn't
      // bounce back into the kid picker.
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("kid");
        window.history.replaceState({}, "", url.toString());
      } catch {}
    }} />;
  }

  const canSubmit =
    !submitting &&
    id.trim().length > 0 &&
    password.length > 0 &&
    (!requireTotp || totpCode.trim().length > 0);

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErr(null);
    const r = await auth.login(id.trim(), password, requireTotp ? totpCode.trim() : undefined);
    setSubmitting(false);
    if (r.ok) {
      // Parent's useAuth refresh fires → app shell replaces this view.
      return;
    }
    if (r.requireTotp) {
      setRequireTotp(true);
      setErr(r.error || null); // null on first-time prompt, error string on bad code
      return;
    }
    setErr(r.error || "sign in failed");
  };

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={onSubmit}>
        <div className="login-brand">
          <div className="login-brand-dot" aria-hidden />
          <span className="login-brand-name">Hub</span>
        </div>

        <h1 className="login-h1">
          Welcome <span className="accent">back.</span>
        </h1>
        <p className="login-sub">Sign in to pick up where you left off.</p>

        {err && <div className="login-err">{err}</div>}

        <div className="login-field">
          <label htmlFor="login-id">Username</label>
          <input
            id="login-id"
            type="text"
            autoComplete="username"
            value={id}
            disabled={submitting || requireTotp}
            onInput={(e) => setId((e.currentTarget as HTMLInputElement).value)}
            spellcheck={false}
            autocapitalize="none"
            required
          />
        </div>

        <div className="login-field">
          <label htmlFor="login-pw">Password</label>
          <input
            id="login-pw"
            type="password"
            autoComplete="current-password"
            value={password}
            disabled={submitting || requireTotp}
            onInput={(e) => setPassword((e.currentTarget as HTMLInputElement).value)}
            required
          />
        </div>

        {requireTotp && (
          <>
            <div className="login-field">
              <label htmlFor="login-totp">2FA code</label>
              <input
                id="login-totp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={totpCode}
                disabled={submitting}
                onInput={(e) => setTotpCode((e.currentTarget as HTMLInputElement).value)}
                maxLength={10}
                required
              />
            </div>
            <p className="login-totp-note">
              Enter your 6-digit code, or a recovery code if you've lost the authenticator.
            </p>
          </>
        )}

        <button className="login-submit" type="submit" disabled={!canSubmit}>
          {submitting
            ? <><span>Signing in…</span></>
            : <>
                <Icon name="arrow-right" size={14} />
                <span>{requireTotp ? "Verify and sign in" : "Sign in"}</span>
              </>
          }
        </button>

        {kidLoginAvailable && (
          <button
            className="login-kid-mode"
            type="button"
            onClick={() => setKidLoginActive(true)}
          >
            <Icon name="people" size={13} />
            <span>Kid mode</span>
          </button>
        )}

        <div className="login-foot">your daily everything</div>
      </form>
    </div>
  );
}
